import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import LogoImage from '../assets/LogoFiverFlow.png';
import DocsSidebar from './docs/DocsSidebar';
import DocsTableOfContents from './docs/DocsTableOfContents';
import HelpfulWidget from './docs/HelpfulWidget';

const navLinks = [
  { label: "Features", href: "/home#features" },
  { label: "Benefits", href: "/home#benefits" },
  { label: "Testimonials", href: "/home#testimonials" },
  { label: "Pricing", href: "/home#pricing" },
  { label: "FAQ", href: "/home#faq" },
  { label: "Docs", href: "/docs" }
];

const DocsLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const [headings, setHeadings] = useState<Array<{ id: string; text: string; level: number }>>([]);

  useEffect(() => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }, [location]);

  // Extract headings from page content for TOC
  useEffect(() => {
    const extractHeadings = () => {
      const elements = document.querySelectorAll('h2, h3');
      const extracted = Array.from(elements).map((el) => ({
        id: el.id || '',
        text: el.textContent || '',
        level: parseInt(el.tagName.replace('H', ''), 10),
      }));
      setHeadings(extracted);
    };

    // Small delay to ensure DOM is rendered
    setTimeout(extractHeadings, 100);
  }, [location.pathname]);

  const handleScroll = (href: string) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (href === '/docs') {
      // Navigate to docs page
      window.location.href = href;
    }
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Top Navigation Bar - EXACT copy from landing */}
      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="sticky top-0 z-50 bg-[rgba(0,0,0,0.4)] backdrop-blur-md border-b border-white/10"
      >
        <div className="max-w-[1300px] mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Brand with Docs label */}
            <Link to="/home" className="flex items-center space-x-3">
              <img src={LogoImage} alt="FiverFlow Logo" className="h-6 w-auto" />
              <span className="text-[#8B5CF6] font-semibold text-sm">DOCS</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link, index) => (
                <motion.a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleScroll(link.href);
                  }}
                  className="text-neutral-300 hover:text-[rgb(var(--fg))] transition-colors"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {link.label}
                </motion.a>
              ))}
            </div>

            {/* Desktop CTA & Mobile Menu Button */}
            <div className="flex items-center gap-4">
              <motion.button
                onClick={() => (window.location.href = '/dashboard')}
                className="hidden lg:inline-flex items-center justify-center rounded-full text-[rgb(var(--fg))] font-medium text-sm px-6 py-3 bg-gradient-to-r from-indigo-500 via-purple-500 to-cyan-400 shadow-[0_20px_80px_rgba(99,102,241,0.6)] hover:shadow-[0_30px_120px_rgba(99,102,241,0.9)] transition-shadow"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Try FiverFlow for free
              </motion.button>

              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden text-[rgb(var(--fg))]"
                aria-label="Toggle menu"
              >
                {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden mt-4 space-y-2"
            >
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleScroll(link.href);
                  }}
                  className="block py-2 text-neutral-300 hover:text-[rgb(var(--fg))] transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <button
                onClick={() => (window.location.href = '/dashboard')}
                className="inline-flex items-center justify-center rounded-full text-[rgb(var(--fg))] font-medium text-sm px-6 py-3 bg-gradient-to-r from-indigo-500 via-purple-500 to-cyan-400 shadow-[0_20px_80px_rgba(99,102,241,0.6)] hover:shadow-[0_30px_120px_rgba(99,102,241,0.9)] transition-shadow mt-2"
              >
                Try FiverFlow for free
              </button>
            </motion.div>
          )}
        </div>
      </motion.nav>

      {/* Three Column Layout */}
      <div className="flex fixed inset-0 top-[73px]">
        {/* Left padding */}
        <div className="w-16 flex-shrink-0" />
        
        {/* Left: Sidebar - Truly Fixed */}
        <div className="w-[260px] flex-shrink-0">
          <DocsSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        </div>

        {/* Middle: Main Content - Only scrollable area */}
        <main className="flex-1 overflow-y-auto bg-[#0A0A0A]">
          <div className="max-w-4xl px-8 py-8 pl-20 pr-8">
            <Outlet />
          </div>
        </main>

        {/* Right: TOC and Helpful Widget - Fixed */}
        <aside className="hidden xl:block w-64 flex-shrink-0 pr-8 pt-8">
          <div className="sticky top-8">
            {headings.length > 0 && <DocsTableOfContents headings={headings} />}
            <HelpfulWidget />
          </div>
        </aside>
        
        {/* Right padding */}
        <div className="hidden xl:block w-16 flex-shrink-0" />
      </div>
    </div>
  );
};

export default DocsLayout;